import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import { SPACE } from '@nxt-insurance/ui-kit/constants'
import { paddingX, paddingY } from '@nxt-insurance/ui-kit/styles/padding'

import { sanityClientLike } from '@/cms/client'
import { spaceEnum } from '@/cms/helperTypes'
import { imageSchema } from '@/cms/image'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

const imageLine = z.object({
  _type: z.literal('imageLine'),
  _key: z.string(),
  itemHeight: spaceEnum.optional(),
  containerMargin: spaceEnum.optional(),
  internalMargin: spaceEnum.optional(),
  lineMargin: spaceEnum.optional(),
  lineHeight: spaceEnum.optional(),
  items: z.array(
    imageSchema.pick({ asset: true }).merge(
      z.object({
        _type: z.literal('Image'),
        _key: z.string(),
      })
    )
  ),
})

type ImageLineSchema = z.infer<typeof imageLine>
type ImageLineItem = ImageLineSchema['items'][number]

const ImageLineItem = (props: { item: ImageLineItem; itemHeight: number }) => {
  const imageProps = useNextSanityImage(sanityClientLike, props.item, {
    imageBuilder: (imageBuilder, options) =>
      imageBuilder
        .height(props.itemHeight)
        .width(Math.floor(options.originalImageDimensions.aspectRatio * props.itemHeight)),
  })
  if (!imageProps) {
    return null
  }
  return <Image {...imageProps} alt="" />
}

export const ImageLine = (props: {
  node: PortableTextTypeComponentProps<unknown>
  width: number
}) => {
  const value = imageLine.parse(props.node.value)
  const itemHeight = SPACE[value.itemHeight ?? 'medium'] || SPACE.medium

  return (
    <div
      className={twMerge(
        'flex w-full flex-row flex-wrap items-center justify-center',
        paddingY({ space: value.containerMargin ?? 'medium' })
      )}
    >
      {value.items.map((item, index) => (
        <div
          className={twMerge(
            paddingY({ space: value.lineMargin ?? 'gutter' }),
            paddingX({
              space: index < value.items.length ? value.internalMargin ?? 'gutter' : undefined,
            }),
            'pl-0'
          )}
          key={item._key}
        >
          <ImageLineItem item={item} itemHeight={itemHeight} />
        </div>
      ))}
    </div>
  )
}
